import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, MenuItem, TextField, IconButton, Box, Button,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { PowerVMRow, NormalizedPowerDefaultAddon, SelectOption, HEADER_CELL_SX, TABLE_CELL_SX, SPACER_CELL_SX, DISK_LIST_SX, rowErrorSX, NO_SPIN_SX, CELL_W, COL } from './types';
import { PowerType, PowerAddon, Disk, Environment } from '../../types';
import { formatCurrency, formatDiskSize } from '../../utils/formatCurrency';

interface PowerVMsTableProps {
  powerVMs: PowerVMRow[];
  environments: Environment[];
  powerTypes: PowerType[];
  powerAddons: PowerAddon[];
  filteredDisks: Disk[];
  disks: Disk[];
  powerDefaultAddons: NormalizedPowerDefaultAddon[];
  pricingReady: boolean;
  onUpdatePowerVM: (id: number, field: string, value: unknown) => void;
  onDeletePowerVM: (id: number) => void;
  onAddPowerVMDisk: (vmId: number) => void;
  onUpdatePowerVMDisk: (vmId: number, idx: number, diskId: number | null) => void;
  onRemovePowerVMDisk: (vmId: number, idx: number) => void;
  isPowerVMRowAvailable: (row: PowerVMRow) => boolean;
  calculatePowerVMCost: (row: PowerVMRow) => number;
}

const isWindowsPower = (powerTypeName: string | undefined): boolean =>
  (powerTypeName?.toLowerCase() ?? '').includes('windows for ibm');

// Returns CPU options: Windows for IBM Power → whole cores 2–16 (step 2); P10 → 4 cores; P20 → 8 cores; others → 10 cores.
const getCPUOptions = (powerTypeName: string | undefined): SelectOption[] => {
  const name = powerTypeName?.toLowerCase() ?? '';
  if (name.includes('windows for ibm')) {
    const options: SelectOption[] = [];
    for (let c = 2; c <= 16; c += 2) options.push({ value: c, label: c });
    return options;
  }
  let maxQuarters = 40; // 10.0 cores default
  if (name.includes('p10')) maxQuarters = 16;       // 4.0 cores
  else if (name.includes('p20')) maxQuarters = 32;  // 8.0 cores
  const options: SelectOption[] = [];
  for (let q = 1; q <= maxQuarters; q++) {
    const val = parseFloat((q * 0.25).toFixed(2));
    options.push({ value: val, label: val.toFixed(2) });
  }
  return options;
};

const getMaxRam = (cpu: number, powerTypeName: string | undefined): number =>
  isWindowsPower(powerTypeName) ? Math.min(cpu * 64, 512) : Math.floor(cpu / 0.25) * 32;

// RAM stepped in 4 GB increments up to per-type cap.
const getRAMOptions = (cpu: number, powerTypeName?: string): SelectOption[] => {
  const maxRam = getMaxRam(cpu, powerTypeName);
  const options: SelectOption[] = [];
  for (let r = 4; r <= maxRam; r += 4) options.push({ value: r, label: r });
  return options;
};

const PowerVMsTable: React.FC<PowerVMsTableProps> = ({
  powerVMs, environments, powerTypes, powerAddons, filteredDisks, disks,
  powerDefaultAddons, pricingReady,
  onUpdatePowerVM, onDeletePowerVM,
  onAddPowerVMDisk, onUpdatePowerVMDisk, onRemovePowerVMDisk,
  isPowerVMRowAvailable, calculatePowerVMCost,
}) => {
  if (powerVMs.length === 0) return null;

  return (
    <Paper sx={{ mb: 3, elevation: 2, border: 1, borderColor: 'grey.300' }}>
      <Box sx={{ p: 1, backgroundColor: 'grey.300', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>IBM Power VMs</Typography>
        {Array.from(new Set(powerVMs.map(v => v.environmentId ?? 1))).map(envId => {
          const env = environments.find(e => e.EnvironmentId === envId);
          const envAddons = powerDefaultAddons
            .filter(da => da.EnvironmentId === envId)
            .map(da => powerAddons.find(a => a.PowerAddonId === da.PowerAddonId)?.PowerAddonName)
            .filter(Boolean)
            .join(', ');
          return (
            <Typography key={envId} variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              <strong>{env?.EnvironmentName} Power VMs{envAddons && ' include the following add-ons:'}</strong>
              {envAddons && ` ${envAddons}`}
            </Typography>
          );
        })}
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.NAME) }}>Name</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.ENV) }}>Env</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.CPU) }}>CPU</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.RAM) }}>RAM</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.OS) }}>Power Type</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, minWidth: COL.STORAGE }}>Extra Storage</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...SPACER_CELL_SX }}></TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.QTY), textAlign: 'center' }}>Qty</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.COST), textAlign: 'right' }}>Cost/mo</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.ACT) }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {powerVMs.map((row) => {
              const available = isPowerVMRowAvailable(row);
              const cost = (!pricingReady && row.savedLineCost != null) ? row.savedLineCost : calculatePowerVMCost(row);
              const powerType = powerTypes.find(pt => pt.PowerTypeId === Number(row.powerTypeId));
              const cpuOptions = getCPUOptions(powerType?.PowerTypeName);
              const maxCpu = cpuOptions.length > 0 ? (cpuOptions[cpuOptions.length - 1]!.value as number) : 10.0;
              const minCpu = cpuOptions.length > 0 ? (cpuOptions[0]!.value as number) : 0.25;
              const effectiveCpu = Math.min(Math.max(row.cpu, minCpu), maxCpu);
              const ramOptions = getRAMOptions(effectiveCpu, powerType?.PowerTypeName);
              const maxRam = ramOptions.length > 0 ? (ramOptions[ramOptions.length - 1]!.value as number) : 512;

              return (
                <TableRow key={row.id} hover sx={!available ? rowErrorSX : undefined}>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.NAME) }}>
                    <TextField size="small" value={row.name} onChange={(e) => onUpdatePowerVM(row.id, 'name', e.target.value)} placeholder="VM Name" fullWidth />
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.ENV) }}>
                    <TextField select size="small" value={row.environmentId} onChange={(e) => onUpdatePowerVM(row.id, 'environmentId', e.target.value)}
                      fullWidth>
                      {environments.map((env) => <MenuItem key={env.EnvironmentId} value={env.EnvironmentId}>{env.EnvironmentName}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.CPU) }}>
                    <TextField select size="small" value={effectiveCpu}
                      onChange={(e) => {
                        const newCpu = Number(e.target.value);
                        onUpdatePowerVM(row.id, 'cpu', newCpu);
                        const newMaxRam = getMaxRam(newCpu, powerType?.PowerTypeName);
                        if (row.ram > newMaxRam) onUpdatePowerVM(row.id, 'ram', newMaxRam);
                      }}
                      fullWidth>
                      {cpuOptions.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.RAM) }}>
                    <TextField select size="small" value={Math.min(row.ram, maxRam)}
                      onChange={(e) => onUpdatePowerVM(row.id, 'ram', e.target.value)}
                      fullWidth>
                      {ramOptions.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.OS) }}>
                    <TextField select size="small" value={row.powerTypeId}
                      onChange={(e) => {
                        const newTypeId = e.target.value;
                        onUpdatePowerVM(row.id, 'powerTypeId', newTypeId);
                        const newType = powerTypes.find(pt => pt.PowerTypeId === Number(newTypeId));
                        const newCpuOpts = getCPUOptions(newType?.PowerTypeName);
                        const newMaxCpu = newCpuOpts.length > 0 ? (newCpuOpts[newCpuOpts.length - 1]!.value as number) : 10.0;
                        const newMinCpu = newCpuOpts.length > 0 ? (newCpuOpts[0]!.value as number) : 0.25;
                        const clampedCpu = Math.min(Math.max(row.cpu, newMinCpu), newMaxCpu);
                        if (clampedCpu !== row.cpu) {
                          onUpdatePowerVM(row.id, 'cpu', clampedCpu);
                          const newMaxRam = getMaxRam(clampedCpu, newType?.PowerTypeName);
                          if (row.ram > newMaxRam) onUpdatePowerVM(row.id, 'ram', newMaxRam);
                        }
                      }}
                      fullWidth>
                      {powerTypes.map((pt) => <MenuItem key={pt.PowerTypeId} value={pt.PowerTypeId}>{pt.PowerTypeName}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, minWidth: COL.STORAGE }}>
                    <Box sx={DISK_LIST_SX}>
                      {(row.disks ?? []).map((diskId, idx) => (
                        <Box key={idx} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField select size="small" value={diskId ?? ''}
                            onChange={(e) => onUpdatePowerVMDisk(row.id, idx, e.target.value ? Number(e.target.value) : null)}
                            sx={{ width: 112 }}>
                            {filteredDisks.map((d) => <MenuItem key={d.DiskId} value={d.DiskId}>{formatDiskSize(d.DiskSize)}</MenuItem>)}
                          </TextField>
                          <IconButton size="small" onClick={() => onRemovePowerVMDisk(row.id, idx)} sx={{ p: 0.25 }}><DeleteIcon sx={{ fontSize: '1rem' }} /></IconButton>
                        </Box>
                      ))}
                      {(row.disks ?? []).length < 3 && (
                        <Button size="small" startIcon={<AddIcon sx={{ fontSize: '0.9rem' }} />} onClick={() => onAddPowerVMDisk(row.id)} sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem', textTransform: 'none' }}>Add</Button>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={SPACER_CELL_SX}></TableCell>
                  <TableCell sx={{ ...CELL_W(COL.QTY), textAlign: 'center' }}>
                    <TextField type="number" size="small" value={row.quantity}
                      onChange={(e) => onUpdatePowerVM(row.id, 'quantity', e.target.value)}
                      inputProps={{ min: 1, step: 1, style: { textAlign: 'center' } }} fullWidth sx={NO_SPIN_SX} />
                  </TableCell>
                  <TableCell sx={{ ...CELL_W(COL.COST), textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {available ? formatCurrency(cost) : <Typography color="error" fontWeight={700}>Unavailable</Typography>}
                  </TableCell>
                  <TableCell sx={{ ...CELL_W(COL.ACT), textAlign: 'right' }}>
                    <IconButton size="small" color="error" onClick={() => onDeletePowerVM(row.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PowerVMsTable;
